/**
 * Instantané d'un DWG pour la fusion de branches (cf. `utils/branch-merge`).
 *
 * Lit une base ODA quelconque (pas forcément celle affichée) :
 *  - l'espace objet : pour chaque entité visible, son handle, sa classe, son
 *    DR_META et une EMPREINTE de contenu ;
 *  - les éléments nommés : calques, styles de texte, types de ligne, styles de
 *    cote, styles de ligne de repère, définitions de blocs et présentations (avec
 *    tout leur espace papier), chacun avec une empreinte.
 *
 * L'empreinte sert à décider « modifié ou non » face à la base : elle doit donc
 * être EXACTE (tous les sommets, tous les textes), jamais échantillonnée — un
 * sommet déplacé non vu serait une modification perdue à la fusion. Les bindings
 * ne donnent pas de sérialisation générique (filers non exposés) : elle est
 * construite par classe, avec l'emprise exacte en repli pour les classes non
 * détaillées. Pour un style, ce sont les propriétés que l'app écrit.
 *
 * Le parcours de l'espace objet rend régulièrement la main au navigateur : un plan
 * de 50 000 entités se lit en plusieurs secondes. Les données d'affichage (libellé,
 * emprise) coûtent 40 % de cette lecture et ne servent qu'aux conflits : elles sont
 * lues à part (`describeEntity`).
 *
 * Les entités du calque UNDO (suppression douce) sont considérées supprimées.
 * Best-effort par entité : une entité illisible reçoit une empreinte d'erreur
 * stable (elle sera vue comme modifiée plutôt qu'oubliée). Chaque erreur est
 * remontée à Sentry, groupée par endroit du code (cf. `SnapshotContext`).
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type {
  DrawingSnapshot,
  EntityDisplay,
  EntitySnapshot,
  NamedRecordKind,
  NamedRecordSnapshot,
} from "@/utils/branch-merge/types";
import {
  fingerprintNumber,
  textAnchors,
} from "@/utils/branch-merge/fingerprint";
import { Logger } from "@/logger";
import { yieldToBrowser } from "@/utils/yield-to-browser";
import { deleteObj } from "../core/delete";
import { DictIterType, OpenMode } from "../core/constants";
import { findEntityByHandle, openAs, getEntityHandle } from "../core/oda";
import { UNDO_LAYER_NAME } from "../database/layers";
import { getColorInfoAndDelete } from "../colors/color";
import { readEntityMeta } from "../meta/xdata";
import { readEntityBbox } from "../meta/extents";
import { readKindAndText } from "../parsing/activity";
import { getPhotoMarkerAttributeValue } from "../photomarker/transform";
import { readMarkerPhotoIds } from "../photomarker/photo-link";

/** Nombre d'exemples (handles, noms) joints à l'événement Sentry d'un endroit. */
const MAX_ERROR_SAMPLES = 10;

/**
 * Contexte d'un instantané.
 *
 * Erreurs de lecture : un plan abîmé peut avoir des centaines d'entités
 * illisibles, d'où un seul événement Sentry par endroit du code, avec le nombre
 * d'erreurs, des exemples et la première exception.
 *
 * Empreintes des blocs anonymes, calculées une fois par bloc.
 */
class SnapshotContext {
  /** Handle d'un bloc anonyme → empreinte de son contenu. */
  readonly anonymousBlocks = new Map<string, string>();

  private bySite = new Map<
    string,
    { count: number; samples: string[]; first: unknown }
  >();

  record(site: string, sample: string, e: unknown): void {
    const entry = this.bySite.get(site);
    if (!entry) {
      this.bySite.set(site, { count: 1, samples: [sample], first: e });
      return;
    }
    entry.count++;
    if (entry.samples.length < MAX_ERROR_SAMPLES) entry.samples.push(sample);
  }

  report(): void {
    for (const [site, { count, samples, first }] of this.bySite) {
      Logger.captureException(first, {
        feature: "branch_merge",
        site: `snapshot.${site}`,
        count,
        samples,
      });
    }
  }
}

const num = fingerprintNumber;

/** Point ODA → texte, en libérant le wrapper. */
function pt(p: { x: number; y: number; z?: number }): string {
  const s = `${num(p.x)},${num(p.y)},${num(p.z ?? 0)}`;
  deleteObj(p as unknown as DrawingModule.OdGePoint3d);
  return s;
}

/** Point(s) qui placent un texte ou un attribut, cf. `textAnchors`. */
function textAnchorParts(text: DrawingModule.OdDbText): string[] {
  return textAnchors(
    text.horizontalMode(),
    text.verticalMode(),
    pt(text.position()),
    pt(text.alignmentPoint()),
  );
}

/** OdString → string, en libérant le wrapper. */
function str(s: DrawingModule.OdString): string {
  const v = s.toString();
  deleteObj(s);
  return v;
}

/** Nom de l'enregistrement de table de symboles `id` (libéré par l'appelant). */
function symbolName(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
): string {
  if (id.isNull()) return "";
  try {
    const record = openAs(id, module.OdDbSymbolTableRecord, OpenMode.kForRead);
    const name = str(record.getName());
    deleteObj(record);
    return name;
  } catch (e) {
    ctx.record("symbolName", getEntityHandle(id), e);
    return "?";
  }
}

/** Nom d'un style de ligne de repère (objet de dictionnaire, pas de table). */
function mleaderStyleName(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
): string {
  if (id.isNull()) return "";
  try {
    const style = openAs(id, module.OdDbMLeaderStyle, OpenMode.kForRead);
    const name = str(style.getName());
    deleteObj(style);
    return name;
  } catch (e) {
    ctx.record("mleaderStyleName", getEntityHandle(id), e);
    return "?";
  }
}

/**
 * Identité du bloc d'une référence : son nom, sauf pour un bloc anonyme (`*U…`
 * des blocs dynamiques, `*D…`…) dont ODA renumérote le nom à chaque écriture du
 * DWG — on prend alors l'empreinte de son contenu.
 */
function blockIdentity(
  module: DrawingModuleType,
  btrId: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
): string {
  const name = symbolName(module, btrId, ctx);
  if (!name.startsWith("*")) return name;
  const handle = getEntityHandle(btrId);
  let content = ctx.anonymousBlocks.get(handle);
  if (content === undefined) {
    content = hash(contentParts(module, btrId, ctx).join("|"));
    ctx.anonymousBlocks.set(handle, content);
  }
  return `anon:${content}`;
}

function blockReferenceParts(
  module: DrawingModuleType,
  ref: DrawingModule.OdDbBlockReference,
  ctx: SnapshotContext,
): string[] {
  const scale = ref.scaleFactors();
  const btrId = ref.blockTableRecord();
  const parts = [
    blockIdentity(module, btrId, ctx),
    pt(ref.position()),
    `${num(scale.sx)},${num(scale.sy)},${num(scale.sz)}`,
    num(ref.rotation()),
  ];
  deleteObj(btrId);
  deleteObj(scale);
  const it = ref.attributeIterator();
  while (!it.done()) {
    const attrId = it.objectId();
    try {
      const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForRead);
      parts.push(`attr:${str(attr.textString())}@${textAnchorParts(attr)}`);
      deleteObj(attr);
    } catch (e) {
      ctx.record("attribute", getEntityHandle(attrId), e);
      parts.push("attr:?");
    }
    deleteObj(attrId);
    it.step(true, true);
  }
  deleteObj(it);
  return parts;
}

function polylineParts(
  module: DrawingModuleType,
  poly: DrawingModule.OdDbPolyline,
): string[] {
  const parts = [
    `closed:${poly.isClosed()}`,
    `w:${num(poly.getConstantWidth())}`,
    `elev:${num(poly.elevation())}`,
  ];
  const n = poly.numVerts();
  for (let i = 0; i < n; i++) {
    const p = new module.OdGePoint2d(0, 0);
    poly.getPointAt(i, p);
    parts.push(`v${i}:${num(p.x)},${num(p.y)}:${num(poly.getBulgeAt(i))}`);
    deleteObj(p);
  }
  return parts;
}

/** Partie de l'empreinte propre à la classe (géométrie et contenu exacts). */
function classParts(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
  ctx: SnapshotContext,
): string[] {
  const line = module.OdDbLine.cast(ent);
  if (line) {
    const parts = [pt(line.startPoint()), pt(line.endPoint())];
    deleteObj(line);
    return parts;
  }
  const poly = module.OdDbPolyline.cast(ent);
  if (poly) {
    const parts = polylineParts(module, poly);
    deleteObj(poly);
    return parts;
  }
  const arc = module.OdDbArc.cast(ent);
  if (arc) {
    const parts = [
      pt(arc.center()),
      num(arc.radius()),
      num(arc.startAngle()),
      num(arc.endAngle()),
    ];
    deleteObj(arc);
    return parts;
  }
  const circle = module.OdDbCircle.cast(ent);
  if (circle) {
    const parts = [pt(circle.center()), num(circle.radius())];
    deleteObj(circle);
    return parts;
  }
  const ellipse = module.OdDbEllipse.cast(ent);
  if (ellipse) {
    const parts = [
      pt(ellipse.center()),
      pt(ellipse.majorAxis()),
      num(ellipse.radiusRatio()),
      num(ellipse.startAngle()),
      num(ellipse.endAngle()),
    ];
    deleteObj(ellipse);
    return parts;
  }
  const text = module.OdDbText.cast(ent);
  if (text) {
    const parts = [
      str(text.textString()),
      ...textAnchorParts(text),
      num(text.height()),
      num(text.rotation()),
      num(text.widthFactor()),
      `${text.horizontalMode()}/${text.verticalMode()}`,
    ];
    deleteObj(text);
    return parts;
  }
  const mtext = module.OdDbMText.cast(ent);
  if (mtext) {
    const parts = [
      str(mtext.contents()),
      pt(mtext.location()),
      num(mtext.textHeight()),
      num(mtext.width()),
      num(mtext.rotation()),
      String(mtext.attachment()),
    ];
    deleteObj(mtext);
    return parts;
  }
  const ref = module.OdDbBlockReference.cast(ent);
  if (ref) {
    const parts = blockReferenceParts(module, ref, ctx);
    deleteObj(ref);
    return parts;
  }
  // Ni emprise ni géométrie calculée : elles dépendent du style de texte, et
  // changer un style « modifierait » toutes les lignes de repère qui l'utilisent.
  const mleader = module.OdDbMLeader.cast(ent);
  if (mleader) {
    const parts: string[] = [];
    try {
      const mt = mleader.mtext();
      if (mt) {
        parts.push(str(mt.contents()), pt(mt.location()));
        deleteObj(mt);
      }
      const styleId = mleader.MLeaderStyle();
      const textStyleId = mleader.textStyleId();
      parts.push(
        mleaderStyleName(module, styleId, ctx),
        symbolName(module, textStyleId, ctx),
        num(mleader.textHeight()),
        num(mleader.arrowSize()),
        num(mleader.scale()),
      );
      deleteObj(styleId);
      deleteObj(textStyleId);
      // Pointes des lignes de repère (index troués : on sonde au-delà).
      const numLines = mleader.numLeaderLines();
      for (let i = 0, found = 0; i < numLines + 10 && found < numLines; i++) {
        const p = new module.OdGePoint3d(0, 0, 0);
        if (mleader.getFirstVertex(i, p) === 0) {
          parts.push(`tip${i}:${num(p.x)},${num(p.y)}`);
          found++;
        }
        deleteObj(p);
      }
    } finally {
      deleteObj(mleader);
    }
    return parts;
  }
  const hatch = module.OdDbHatch.cast(ent);
  if (hatch) {
    const parts = [
      str(hatch.patternName()),
      num(hatch.patternScale()),
      num(hatch.patternAngle()),
      `solid:${hatch.isSolidFill()}`,
      `loops:${hatch.numLoops()}`,
    ];
    deleteObj(hatch);
    return [...parts, bboxPart(module, ent)];
  }
  // Classe non détaillée : l'emprise exacte en repli.
  return [bboxPart(module, ent)];
}

function bboxPart(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
): string {
  const b = readEntityBbox(module, ent);
  return b
    ? `bbox:${num(b.minX)},${num(b.minY)},${num(b.maxX)},${num(b.maxY)}`
    : "bbox:none";
}

/** Propriétés communes à toute entité (calque, couleur, type de ligne…). */
function commonParts(ent: DrawingModule.OdDbEntity, layer: string): string[] {
  return [
    layer,
    JSON.stringify(getColorInfoAndDelete(ent.color())),
    str(ent.linetype()),
    String(ent.lineWeight()),
    num(ent.linetypeScale()),
  ];
}

/** Hachage FNV-1a 32 bits ×2 (empreinte courte, collisions négligeables ici). */
function hash(s: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995);
  }
  return `${(h1 >>> 0).toString(16)}${(h2 >>> 0).toString(16)}`;
}

/** Empreinte d'une entité (stable, sans handle ni DR_META). */
function entityFingerprint(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
  className: string,
  layer: string,
  ctx: SnapshotContext,
): string {
  try {
    return hash(
      [
        className,
        ...commonParts(ent, layer),
        ...classParts(module, ent, ctx),
      ].join("|"),
    );
  } catch (e) {
    ctx.record("fingerprint", className, e);
    return `unreadable:${className}`;
  }
}

/**
 * Nom de la classe ODA. `isA()` rend le descripteur GLOBAL de la classe (non
 * possédé) : le libérer détruirait la classe pour tout le moteur (lectures de
 * DWG suivantes en « null function »).
 */
function classNameOf(ent: DrawingModule.OdDbEntity): string {
  return str(ent.isA().name());
}

/**
 * Parcourt les entités d'une définition de bloc (espace objet, espace papier ou
 * bloc), un pas par entité : le consommateur peut s'interrompre entre deux pas, ou
 * arrêter (itérateur libéré). `id` et `ent` sont libérés après l'appel de `visit`.
 */
function* entitySteps(
  module: DrawingModuleType,
  btrId: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
  visit: (
    id: DrawingModule.OdDbObjectId,
    ent: DrawingModule.OdDbEntity,
  ) => void,
): Generator<void> {
  const rec = openAs(btrId, module.OdDbBlockTableRecord, OpenMode.kForRead);
  // Pas de `continue` dans la boucle : `iter.step()` doit toujours être atteint
  // (cf. analysis-scan.ts — itérateur figé = gel de l'app).
  const iter = rec.newIterator(true, true, false);
  try {
    iter.start(true, true);
    while (!iter.done()) {
      const id = iter.objectId();
      try {
        const ent = iter.entity(OpenMode.kForRead, false);
        try {
          visit(id, ent);
        } finally {
          deleteObj(ent);
        }
      } catch (e) {
        ctx.record("entity", getEntityHandle(id), e);
      }
      deleteObj(id);
      yield;
      iter.step(true, true);
    }
  } finally {
    deleteObj(iter);
    deleteObj(rec);
  }
}

function forEachEntity(
  module: DrawingModuleType,
  btrId: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
  visit: (
    id: DrawingModule.OdDbObjectId,
    ent: DrawingModule.OdDbEntity,
  ) => void,
): void {
  for (const _ of entitySteps(module, btrId, ctx, visit));
}

/** Empreintes des entités visibles d'un bloc ou d'un espace papier, dans l'ordre. */
function contentParts(
  module: DrawingModuleType,
  btrId: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
): string[] {
  const parts: string[] = [];
  forEachEntity(module, btrId, ctx, (_id, ent) => {
    const layer = str(ent.layer());
    if (layer === UNDO_LAYER_NAME) return;
    parts.push(entityFingerprint(module, ent, classNameOf(ent), layer, ctx));
  });
  return parts;
}

/** Numéro d'un marqueur photo : seulement si son libellé EST un nombre. */
function markerNumberOf(label: string | null): number | null {
  if (!label) return null;
  const trimmed = label.trim();
  return /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) : null;
}

function snapshotEntity(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
  ent: DrawingModule.OdDbEntity,
  ctx: SnapshotContext,
): EntitySnapshot | null {
  const layer = str(ent.layer());
  if (layer === UNDO_LAYER_NAME) return null;

  const className = classNameOf(ent);
  const meta = readEntityMeta(module, id);
  const markerLabel = getPhotoMarkerAttributeValue(module, id);
  return {
    handle: getEntityHandle(id),
    className,
    uid: meta?.entityUid ?? null,
    fingerprint: entityFingerprint(module, ent, className, layer, ctx),
    observationId: meta?.observationId ?? null,
    markerNumber: markerNumberOf(markerLabel),
    photoIds: markerLabel === null ? [] : readMarkerPhotoIds(module, id),
    layer,
  };
}

/** Données d'affichage d'une entité en conflit (cf. en-tête), vide si elle est illisible. */
export function describeEntity(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  handle: string,
): EntityDisplay {
  const ctx = new SnapshotContext();
  const id = findEntityByHandle(module, db, handle);
  if (!id) return {};
  try {
    const ent = openAs(id, module.OdDbEntity, OpenMode.kForRead);
    try {
      return {
        label:
          getPhotoMarkerAttributeValue(module, id) ??
          readKindAndText(module, id, ent).text ??
          undefined,
        blockName: blockNameOf(module, ent, ctx),
        bbox: readEntityBbox(module, ent) ?? insertionBbox(module, ent),
      };
    } finally {
      deleteObj(ent);
    }
  } catch (e) {
    ctx.record("describeEntity", handle, e);
    return {};
  } finally {
    deleteObj(id);
    ctx.report();
  }
}

/**
 * Repli d'emprise (cadre et zoom de l'écran de fusion) : le point d'insertion
 * d'une référence de bloc dont l'emprise n'est pas calculable (bloc vide ou
 * fait d'éléments sans géométrie).
 */
function insertionBbox(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
): EntitySnapshot["bbox"] {
  const ref = module.OdDbBlockReference.cast(ent);
  if (!ref) return null;
  const p = ref.position();
  const bbox = { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y };
  deleteObj(p);
  deleteObj(ref);
  return bbox;
}

/** Nom du bloc d'une référence, pour l'affichage (pas les blocs anonymes `*U…`). */
function blockNameOf(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
  ctx: SnapshotContext,
): string | undefined {
  const ref = module.OdDbBlockReference.cast(ent);
  if (!ref) return undefined;
  const btrId = ref.blockTableRecord();
  try {
    const name = symbolName(module, btrId, ctx);
    return name && !name.startsWith("*") ? name : undefined;
  } finally {
    deleteObj(btrId);
    deleteObj(ref);
  }
}

// ==================== Éléments nommés ====================

/**
 * Lit les enregistrements d'une table de symboles. `parts` renvoie l'empreinte
 * d'un enregistrement, ou null pour l'ignorer (calques internes, blocs anonymes…).
 */
function snapshotTable(
  module: DrawingModuleType,
  kind: NamedRecordKind,
  tableId: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
  parts: (
    record: DrawingModule.OdDbSymbolTableRecord,
    name: string,
  ) => string[] | null,
): NamedRecordSnapshot[] {
  const records: NamedRecordSnapshot[] = [];
  const table = openAs(tableId, module.OdDbSymbolTable, OpenMode.kForRead);
  const it = table.newIterator(true, true);
  while (!it.done()) {
    const record = it.getRecord(OpenMode.kForRead, false);
    let name = "?";
    try {
      name = str(record.getName());
      const p = name ? parts(record, name) : null;
      if (p) records.push({ kind, name, fingerprint: hash(p.join("|")) });
    } catch (e) {
      ctx.record(kind, name, e);
    } finally {
      deleteObj(record);
    }
    it.step(true, true);
  }
  deleteObj(it);
  deleteObj(table);
  deleteObj(tableId);
  return records;
}

/** Parcourt un dictionnaire nommé (styles de repère, présentations). */
function snapshotDictionary(
  module: DrawingModuleType,
  kind: NamedRecordKind,
  dictId: DrawingModule.OdDbObjectId,
  ctx: SnapshotContext,
  parts: (id: DrawingModule.OdDbObjectId, name: string) => string[] | null,
): NamedRecordSnapshot[] {
  const records: NamedRecordSnapshot[] = [];
  if (dictId.isNull()) {
    deleteObj(dictId);
    return records;
  }
  const dict = openAs(dictId, module.OdDbDictionary, OpenMode.kForRead);
  const it = dict.newIterator(DictIterType.kDictCollated);
  while (!it.done()) {
    const name = str(it.name());
    const id = it.objectId();
    try {
      const p = parts(id, name);
      if (p) records.push({ kind, name, fingerprint: hash(p.join("|")) });
    } catch (e) {
      ctx.record(kind, name, e);
    } finally {
      deleteObj(id);
    }
    it.next();
  }
  deleteObj(it);
  deleteObj(dict);
  deleteObj(dictId);
  return records;
}

function snapshotRecords(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  ctx: SnapshotContext,
): NamedRecordSnapshot[] {
  const layers = snapshotTable(
    module,
    "layer",
    db.getLayerTableId(),
    ctx,
    (record, name) => {
      if (name.startsWith("__DR_") || name === UNDO_LAYER_NAME) return null;
      const layer = module.OdDbLayerTableRecord.cast(record);
      try {
        return [
          JSON.stringify(getColorInfoAndDelete(layer.color())),
          String(layer.isOff()),
          String(layer.isFrozen()),
          String(layer.isLocked()),
          String(layer.isPlottable()),
          String(layer.lineWeight()),
          str(layer.description()),
        ];
      } finally {
        deleteObj(layer);
      }
    },
  );

  // Propriétés écrites par setTextStyleProperties (database/textStyle.ts).
  const textStyles = snapshotTable(
    module,
    "textStyle",
    db.getTextStyleTableId(),
    ctx,
    (record) => {
      const style = module.OdDbTextStyleTableRecord.cast(record);
      try {
        if (style.isShapeFile()) return null;
        return [
          str(style.fileName()),
          str(style.bigFontFileName()),
          num(style.textSize()),
          num(style.xScale()),
          num(style.obliquingAngle()),
          String(style.isVertical()),
        ];
      } finally {
        deleteObj(style);
      }
    },
  );

  const linetypes = snapshotTable(
    module,
    "linetype",
    db.getLinetypeTableId(),
    ctx,
    (record) => {
      const lt = module.OdDbLinetypeTableRecord.cast(record);
      try {
        const n = lt.numDashes();
        const dashes: string[] = [];
        for (let i = 0; i < n; i++) dashes.push(num(lt.dashLengthAt(i)));
        return [str(lt.comments()), num(lt.patternLength()), dashes.join(",")];
      } finally {
        deleteObj(lt);
      }
    },
  );

  // Variables posées par ensureDimensionStyle (dimensions/style.ts).
  const dimStyles = snapshotTable(
    module,
    "dimStyle",
    db.getDimStyleTableId(),
    ctx,
    (record) => {
      const ds = module.OdDbDimStyleTableRecord.cast(record);
      const textStyleId = ds.dimtxsty();
      try {
        return [
          num(ds.dimtxt()),
          num(ds.dimasz()),
          num(ds.dimexo()),
          num(ds.dimexe()),
          num(ds.dimgap()),
          num(ds.dimscale()),
          String(ds.dimtad()),
          String(ds.dimtih()),
          String(ds.dimtoh()),
          String(ds.dimdec()),
          String(ds.dimdsep()),
          String(ds.dimzin()),
          symbolName(module, textStyleId, ctx),
        ];
      } finally {
        deleteObj(textStyleId);
        deleteObj(ds);
      }
    },
  );

  // Définitions de blocs nommés : ni blocs anonymes (cotes, hachures…), ni blocs
  // de présentation (fusionnés avec leur présentation), ni xréfs.
  const blocks = snapshotTable(
    module,
    "block",
    db.getBlockTableId(),
    ctx,
    (record, name) => {
      if (name.startsWith("*")) return null;
      const btr = module.OdDbBlockTableRecord.cast(record);
      try {
        if (
          btr.isLayout() ||
          btr.isAnonymous() ||
          btr.isFromExternalReference()
        )
          return null;
        const btrId = btr.objectId();
        try {
          return [pt(btr.origin()), ...contentParts(module, btrId, ctx)];
        } finally {
          deleteObj(btrId);
        }
      } finally {
        deleteObj(btr);
      }
    },
  );

  // Propriétés posées par ensureDRMLeaderStyle (leaders/mleaderStyle.ts).
  const mleaderStyles = snapshotDictionary(
    module,
    "mleaderStyle",
    db.getMLeaderStyleDictionaryId(false),
    ctx,
    (id) => {
      const style = openAs(id, module.OdDbMLeaderStyle, OpenMode.kForRead);
      const textStyleId = style.textStyleId();
      try {
        return [
          num(style.textHeight()),
          num(style.arrowSize()),
          num(style.landingGap()),
          num(style.doglegLength()),
          String(style.enableDogleg()),
          String(style.enableLanding()),
          String(style.contentType()),
          symbolName(module, textStyleId, ctx),
        ];
      } finally {
        deleteObj(textStyleId);
        deleteObj(style);
      }
    },
  );

  // Présentations : réglages d'impression + tout l'espace papier.
  const layouts = snapshotDictionary(
    module,
    "layout",
    db.getLayoutDictionaryId(false),
    ctx,
    (id, name) => {
      if (name === "Model") return null;
      const layout = openAs(id, module.OdDbLayout, OpenMode.kForRead);
      const btrId = layout.getBlockTableRecordId();
      try {
        return [
          str(layout.getCanonicalMediaName()),
          String(layout.plotRotation()),
          String(layout.plotPaperUnits()),
          ...contentParts(module, btrId, ctx),
        ];
      } finally {
        deleteObj(btrId);
        deleteObj(layout);
      }
    },
  );

  return [
    ...layers,
    ...textStyles,
    ...linetypes,
    ...dimStyles,
    ...blocks,
    ...mleaderStyles,
    ...layouts,
  ];
}

export interface SnapshotOptions {
  /** Consulté à chaque reprise : `true` abandonne la lecture (`SnapshotCancelled`). */
  isCancelled?: () => boolean;
  /** Nombre d'entités lues, à chaque reprise. */
  onProgress?: (count: number) => void;
}

/** Lecture abandonnée (session de fusion fermée entre-temps). */
export class SnapshotCancelled extends Error {}

/** Durée d'une tranche de lecture : la page reste fluide pendant le parcours. */
const SLICE_MS = 30;

/** Instantané de l'espace objet et des éléments nommés d'une base. */
export async function snapshotDatabase(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  options: SnapshotOptions = {},
): Promise<DrawingSnapshot> {
  const ctx = new SnapshotContext();
  try {
    const entities: EntitySnapshot[] = [];
    const msId = db.getModelSpaceId();
    try {
      let sliceStart = performance.now();
      for (const _ of entitySteps(module, msId, ctx, (id, ent) => {
        const snap = snapshotEntity(module, id, ent, ctx);
        if (snap) entities.push(snap);
      })) {
        if (performance.now() - sliceStart < SLICE_MS) continue;
        options.onProgress?.(entities.length);
        await yieldToBrowser();
        if (options.isCancelled?.()) throw new SnapshotCancelled();
        sliceStart = performance.now();
      }
    } finally {
      deleteObj(msId);
    }
    return { entities, records: snapshotRecords(module, db, ctx) };
  } finally {
    ctx.report();
  }
}
